import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { clsx } from 'clsx';

/**
 * A chip with an arrow is a PICKER, never a label pretending to be one
 * (BRIEF §4.8). This is the desk's one dropdown: the chip the board draws,
 * and under it a real list of the choices, each doing what it says.
 *
 * Rendered inside the page's `.maie` scope, so the menu uses kit tokens.
 */
export interface PickerOption<V extends string> {
  value: V;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

export function PickerChip<V extends string>({
  label,
  options,
  value,
  onChange,
  kind = 'outline',
  small = true,
  className,
  style,
  menuAlign = 'left',
  ariaLabel,
  asText = false,
  textClassName,
  triggerClassName,
}: {
  label: ReactNode;
  options: ReadonlyArray<PickerOption<V>>;
  value?: V | null;
  onChange: (v: V) => void;
  kind?: 'outline' | 'brand';
  small?: boolean;
  className?: string;
  style?: CSSProperties;
  menuAlign?: 'left' | 'right';
  ariaLabel?: string;
  /** Render the trigger as inline text (a `▾` sentence) rather than a chip. */
  asText?: boolean;
  textClassName?: string;
  /** Replace the trigger's classes outright — e.g. a desk action chip (`dchip`). */
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', ...style }} className={className}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={
          triggerClassName ??
          (asText
            ? clsx('rc-textpick', textClassName)
            : clsx('chip', kind === 'brand' ? 'chip-brand w6' : 'chip-outline', small && 'chip-sm'))
        }
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </button>
      {open ? (
        <span
          role="listbox"
          className="rc-menu"
          style={menuAlign === 'right' ? { right: 0 } : { left: 0 }}
        >
          {options.length ? (
            options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                disabled={o.disabled}
                className={clsx('rc-menu-item', o.value === value && 'on')}
                onClick={() => {
                  setOpen(false);
                  onChange(o.value);
                }}
              >
                <span className="grow">{o.label}</span>
                {o.hint ? <span className="t-cap mut">{o.hint}</span> : null}
              </button>
            ))
          ) : (
            <span className="t-cap mut" style={{ display: 'block', padding: '8px 12px' }}>Nothing to pick from yet.</span>
          )}
        </span>
      ) : null}
    </span>
  );
}
